import { useNavigate } from 'react-router-dom';
import { 
  Bell, Trash2, FileText, UserCheck, UserPlus, 
  RefreshCw, X, Edit, ChevronUp, HelpCircle, Calendar as CalendarIcon, Check
} from 'lucide-react';
import StatusDropdown from '../components/StatusDropdown';
import PatientBusyAlert from '../components/PatientBusyAlert';
import Modal from '../components/Modal';
import Spinner from '../components/Spinner';
import { useWaitingRoom } from '../hooks/useWaitingRoom';
import './WaitingRoom.css';

const getLocalYYYYMMDD = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const WaitingRoom = () => {
  const navigate = useNavigate();
  const activeStaff = JSON.parse(localStorage.getItem('gdt_active_staff') || '{}');
  const isAdmin = activeStaff?.type === 'Administrador' || activeStaff?.type === 'Secretaria/o';
  const staffName = isAdmin ? '' : (activeStaff.full_name || '');

  const {
    viewDate, setViewDate,
    isReduced, setIsReduced,
    loading,
    professionals,
    selectedProfessional, setSelectedProfessional,
    searchTerm, setSearchTerm,
    isAdmitModalOpen, setIsAdmitModalOpen,
    admitData, setAdmitData,
    settings, setSettings,
    consultorios,
    attendedPatient, setAttendedPatient,
    filteredPatients,
    handleManualRefresh,
    updateStatus,
    handleAssignConsultory,
    handleAttendPatient,
    handleCall,
    handleQuickAdmit,
    deleteAppointment,
    sortBy, setSortBy
  } = useWaitingRoom(staffName);

  const getProfessionalTheme = (name) => {
    const themes = {
      'TODOS': { color: '#00e5ff', initials: '🌍' },
      'CONSULTORIOS EXTERNOS': { color: '#7e57c2', initials: 'CE' },
      'ECOGRAFIAS .': { color: '#ffee58', initials: 'E' },
      'FONOAUDIOLOGA .': { color: '#ffa726', initials: 'F' },
      'NEUMONOLOGO .': { color: '#fdd835', initials: 'N' },
      'NEUROLOGO .': { color: '#d4e157', initials: 'N' },
      'OTORRINO .': { color: '#66bb6a', initials: 'O' },
      'PSICOLOGIA .': { color: '#d4e157', initials: 'P' },
      'RAYOS X': { color: '#26c6da', initials: 'RX' }
    };
    return themes[name] || { color: '#00e5ff', initials: name?.substring(0, 2).toUpperCase() || 'P' };
  };

  const formatViewDate = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length !== 3) return dateStr;
    const dateObj = new Date(parts[0], parts[1] - 1, parts[2]);
    const todayStr = getLocalYYYYMMDD();
    const isToday = dateStr === todayStr;
    return `${dateObj.toLocaleDateString('es-ES', { day: 'numeric', month: 'numeric', year: 'numeric' })}${isToday ? ' (Hoy)' : ''}`;
  };

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    try {
      const date = new Date(isoString);
      return date.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', hour12: false }) + ' hs';
    } catch {
      return '-';
    }
  };

  const getWaitTime = (p) => {
    if (!p.arrival_time) return '-';
    const start = new Date(p.arrival_time);
    let end = new Date();
    
    if (p.status === 'atendido' && p.attended_time) {
      end = new Date(p.attended_time);
    } else if (p.status === 'en-consultorio' || p.status === 'atendido') {
      end = new Date(p.updated_at);
    }
    
    const diffMs = end - start;
    if (diffMs < 0) return '0 min';
    const diffMins = Math.floor(diffMs / 60000);
    return `${diffMins} min`;
  };

  const renderAdmitModal = () => {
    return (
      <Modal
        isOpen={isAdmitModalOpen}
        onClose={() => { setIsAdmitModalOpen(false); setAdmitData({ patient_name: '', patient_dni: '', professional_name: staffName || '' }); }}
        title={admitData.id ? 'MODIFICAR ADMISIÓN' : 'NUEVA ADMISIÓN ESPONTÁNEA'}
        icon={UserPlus}
        iconColor="cyan"
        footer={
          <>
            <button className="btn-cancel-modal" onClick={() => { setIsAdmitModalOpen(false); setAdmitData({ patient_name: '', patient_dni: '', professional_name: staffName || '' }); }}>CANCELAR</button>
            <button className="btn-save-appointment" onClick={handleQuickAdmit}>{admitData.id ? 'GUARDAR CAMBIOS' : 'ADMITIR AHORA'}</button>
          </>
        }
      >
        <div className="form-grid">
          <div className="form-group full">
            <label>Nombre del Paciente</label>
            <input 
              type="text" 
              value={admitData.patient_name}
              onChange={(e) => setAdmitData({...admitData, patient_name: e.target.value})}
              placeholder="Ej: Juan Pérez"
            />
          </div>
          <div className="form-group">
            <label>DNI</label>
            <input 
              type="text" 
              value={admitData.patient_dni}
              onChange={(e) => setAdmitData({...admitData, patient_dni: e.target.value})}
              placeholder="Ej: 12345678"
            />
          </div>
          <div className="form-group">
            <label>Profesional / Especialidad</label>
            {staffName ? (
              <input 
                type="text" 
                readOnly 
                value={staffName} 
                style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', color: '#cbd5e1' }}
              />
            ) : (
              <select 
                value={admitData.professional_name}
                onChange={(e) => setAdmitData({...admitData, professional_name: e.target.value})}
                style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.05)', color: 'white' }}
              >
                <option value="">Seleccione profesional...</option>
                {professionals.map(prof => (
                  <option key={prof.id} value={prof.name}>{prof.name}</option>
                ))}
              </select>
            )}
          </div>
        </div>
      </Modal>
    );
  };

  if (!selectedProfessional) {
    return (
      <div className="waiting-room-page">
        <div className="page-header">
          <div className="title-section">
            <h1>SALA DE ESPERA VIRTUAL</h1>
            <p>Seleccione una agenda para gestionar los pacientes en espera</p>
          </div>
          <div className="header-actions">
            <button 
              className="btn-primary" 
              onClick={() => {
                setAdmitData({ patient_name: '', patient_dni: '', professional_name: staffName || '' });
                setIsAdmitModalOpen(true);
              }}
            >
              <UserPlus size={18} /> ADMISIÓN ESPONTÁNEA
            </button>
            <button className="btn-secondary" onClick={handleManualRefresh}>
              <RefreshCw size={18} className={loading ? "animate-spin" : ""} /> ACTUALIZAR
            </button>
          </div>
        </div>

        <div className="professional-grid-wr">
          {/* General ununified view card */}
          <div className="prof-card-wr glass-panel-premium" onClick={() => setSelectedProfessional('TODOS')} style={{ border: '1px dashed rgba(0, 229, 255, 0.4)' }}>
            <div className="prof-card-icon" style={{ backgroundColor: '#00e5ff' }}>
              🌍
            </div>
            <div className="prof-card-info">
              <h3>TODAS LAS AGENDAS</h3>
              <span className="prof-specialty">Vista General Unificada</span>
            </div>
            <button className="btn-enter-room" style={{ borderColor: 'rgba(0, 229, 255, 0.4)', color: '#00e5ff' }}>VER SALA GENERAL</button>
          </div>

          {professionals.map(prof => (
            <div key={prof.id} className="prof-card-wr glass-panel-premium" onClick={() => setSelectedProfessional(prof.name)}>
              <div className="prof-card-icon" style={{ backgroundColor: getProfessionalTheme(prof.name).color }}>
                {getProfessionalTheme(prof.name).initials}
              </div>
              <div className="prof-card-info">
                <h3>{prof.name}</h3>
                <span className="prof-specialty">Agenda de Turnos</span>
              </div>
              <button className="btn-enter-room">VER SALA DE ESPERA VIRTUAL</button>
            </div>
          ))}
        </div>
        {renderAdmitModal()}
      </div>
    );
  }

  return (
    <div className="waiting-room-page">
      <div className="room-header-premium glass-panel">
        <div className="room-info-top">
          <div className="prof-badge-sm" style={{ backgroundColor: getProfessionalTheme(selectedProfessional).color }}>
            {getProfessionalTheme(selectedProfessional).initials}
          </div>
          <div className="room-title-text">
            <h2>
              Sala de Espera: 
              {!staffName ? (
                <select
                  className="prof-switch-select"
                  value={selectedProfessional}
                  onChange={(e) => {
                    setSelectedProfessional(e.target.value);
                    setSearchTerm('');
                  }}
                >
                  <option value="TODOS">TODAS LAS AGENDAS (General)</option>
                  {professionals.map(p => (
                    <option key={p.id} value={p.name}>{p.name}</option>
                  ))}
                </select>
              ) : (
                <span className="highlight-blue" style={{ marginLeft: '0.5rem' }}>{selectedProfessional}</span>
              )}
            </h2>
            <p>Viendo Fecha: <span className="highlight-date">{formatViewDate(viewDate)}</span></p>
          </div>
        </div>

        <div className="room-controls-bar">
          <div className="search-bar-wr">
            <input
              type="text"
              placeholder="Buscar paciente..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input-wr"
            />
          </div>
          <div className="action-buttons-group">
            <button className="btn-wr-header" style={{ background: 'rgba(255,255,255,0.1)' }} onClick={handleManualRefresh}>
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
              <span>ACTUALIZAR</span>
            </button>
            <div className="btn-wr-header blue" style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <CalendarIcon size={14} />
              <span>CAMBIAR FECHA</span>
              <input
                type="date"
                value={viewDate}
                onChange={(e) => setViewDate(e.target.value)}
                onClick={(e) => {
                  try {
                    if (typeof e.target.showPicker === 'function') {
                      e.target.showPicker();
                    }
                  } catch (err) {}
                }}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  opacity: 0,
                  cursor: 'pointer',
                  zIndex: 2
                }}
              />
            </div>
            {!staffName && (
              <button className="btn-wr-header gray" onClick={() => setSelectedProfessional(null)}>SELECCIONAR OTRO PROFESIONAL</button>
            )}
            <button className="btn-wr-header green" onClick={() => setIsReduced(!isReduced)}>
              {isReduced ? 'VER LISTADO COMPLETO' : 'VER LISTADO REDUCIDO'}
            </button>
            <button 
              className="btn-wr-header cyan" 
              onClick={() => {
                const url = selectedProfessional && selectedProfessional !== 'TODOS'
                  ? `#/patients-caller?professional=${encodeURIComponent(selectedProfessional)}`
                  : '#/patients-caller';
                window.open(url, '_blank');
              }}
            >
              🖥️ PANTALLA LLAMADOR
            </button>
          </div>
          
          <div className="toggle-controls-group">
            <div className="toggle-item">
              <input type="checkbox" id="sound" checked={settings.playSound} onChange={(e) => setSettings({...settings, playSound: e.target.checked})} />
              <label htmlFor="sound">Reproducir Sonido con cada Presente</label>
            </div>
            <div className="toggle-item">
              <input type="checkbox" id="caller" checked={settings.useCaller} onChange={(e) => setSettings({...settings, useCaller: e.target.checked})} />
              <label htmlFor="caller">Utilizar Llamador a Pacientes</label>
            </div>
            <HelpCircle size={20} className="help-icon-wr" />
          </div>
        </div>
      </div>

      <div className="wr-main-content card glass-panel-premium">
        <table className="wr-table-premium">
          <thead>
            <tr>
              <th onClick={() => setSortBy('time')} style={{cursor: 'pointer'}} title="Ordenar por Hora de Turno">
                {sortBy === 'time' ? <ChevronUp size={14} /> : <span style={{display: 'inline-block', width: '14px'}}></span>} Hora Turno
              </th>
              <th>Paciente</th>
              {!isReduced && <th>Tipo Turno</th>}
              <th>Consultorio</th>
              <th>Estado</th>
              {!isReduced && <th onClick={() => setSortBy('arrival')} style={{cursor: 'pointer'}} title="Ordenar por Hora de Llegada">
                {sortBy === 'arrival' ? <ChevronUp size={14} /> : <span style={{display: 'inline-block', width: '14px'}}></span>} Hora Llegada
              </th>}
              {!isReduced && <th>Min. Espera</th>}
              {!isReduced && <th>Atendido</th>}
              {!isReduced && <th>Evoluc.</th>}
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={isReduced ? 5 : 10} className="text-center-p"><Spinner text="Cargando sala..." /></td></tr>
            ) : filteredPatients.length === 0 ? (
              <tr>
                <td colSpan={isReduced ? 5 : 10} className="empty-room-msg">
                  El Profesional {selectedProfessional} no tiene turnos el día: {formatViewDate(viewDate)}
                </td>
              </tr>
            ) : (
              filteredPatients.map((p) => {
                return (
                  <tr key={p.id} className={`wr-row status-${p.status}`}>
                    <td className="time-cell-wr">{p.appointment_time?.substring(0, 5)} hs</td>
                    <td>
                      <div className="patient-cell-wr">
                        <span className="p-name">{p.patient_name}</span>
                        <span className="p-dni">DNI: {p.patient_dni || '---'}</span>
                        {(p.admin_notes || p.notes || (isReduced && p.study_type && p.study_type !== 'CONSULTA GENERAL')) && (
                          <div style={{ fontSize: '0.75rem', color: '#ffb020', marginTop: '4px', lineHeight: '1.2', maxWidth: '200px', whiteSpace: 'normal', wordWrap: 'break-word' }}>
                            {isReduced && p.study_type && p.study_type !== 'CONSULTA GENERAL' && (
                              <div style={{color: '#00e5ff', marginBottom: '2px'}}>{p.study_type}</div>
                            )}
                            {(p.admin_notes || p.notes) && (
                              <div><strong style={{ opacity: 0.8 }}>Obs:</strong> {p.admin_notes || p.notes}</div>
                            )}
                          </div>
                        )}
                        <PatientBusyAlert appointment={p} />
                      </div>
                    </td>
                    {!isReduced && <td className="type-cell-wr">{p.study_type || 'CONSULTA'}</td>}
                    <td>
                      <select 
                        className="consultory-select-wr"
                        value={p.consultorio_name || ''}
                        onChange={(e) => handleAssignConsultory(p.id, e.target.value)}
                      >
                        <option value="">Seleccionar...</option>
                        {consultorios.map((c, idx) => (
                          <option key={idx} value={c.name}>{c.name}</option>
                        ))}
                      </select>
                    </td>
                    <td><StatusDropdown value={p.status} onChange={(newStatus) => updateStatus(p.id, newStatus, p.patient_name)} /></td>
                    {!isReduced && <td className="text-center">{formatTime(p.arrival_time)}</td>}
                    {!isReduced && <td className="text-center">{getWaitTime(p)}</td>}
                    {!isReduced && <td className="text-center">{p.status === 'atendido' ? formatTime(p.attended_time || p.updated_at) : '-'}</td>}
                    {!isReduced && <td className="text-center"><FileText size={16} className="icon-btn-wr" onClick={() => p.patient_id ? navigate(`/patients/${p.patient_id}`) : null} /></td>}
                    <td>
                      <div className="wr-actions-flex">
                        {p.status === 'pendiente' && (
                          <button className="btn-wr-action admit" onClick={() => updateStatus(p.id, 'presente', p.patient_name)}>
                            <UserCheck size={14} /> ADMITIR
                          </button>
                        )}
                        {(p.status === 'presente' || p.status === 'en-llamado') && (
                          <button className="btn-wr-action call" onClick={() => handleCall(p)}>
                            <Bell size={14} /> LLAMAR
                          </button>
                        )}
                        {p.status === 'en-llamado' && (
                          <button className="btn-wr-action enter" onClick={() => updateStatus(p.id, 'en-consultorio', p.patient_name)}>
                            🚪 INGRESÓ
                          </button>
                        )}
                        {(p.status === 'en-llamado' || p.status === 'en-consultorio') && (
                          <button className="btn-wr-action attend" onClick={() => handleAttendPatient(p)}>
                            ✓ ATENDIDO
                          </button>
                        )}
                        <div className="wr-more-actions">
                          <button 
                            className="btn-icon-sm"
                            onClick={() => {
                              setAdmitData({
                                id: p.id,
                                patient_name: p.patient_name,
                                patient_dni: p.patient_dni || '',
                                professional_name: p.professional_name || ''
                              });
                              setIsAdmitModalOpen(true);
                            }}
                          >
                            <Edit size={14} />
                          </button>
                          <button className="btn-icon-sm delete" onClick={() => deleteAppointment(p.id)}><Trash2 size={14} /></button>
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {renderAdmitModal()}

      {/* Attended patient modal */}
      <Modal
        isOpen={!!attendedPatient}
        onClose={() => setAttendedPatient(null)}
        title="CONSULTA FINALIZADA"
        icon={Check}
        theme="premium"
        footer={
          <>
            <button 
              className="btn-cancel-modal" 
              onClick={() => setAttendedPatient(null)}
              style={{ minWidth: '100px' }}
            >
              CERRAR
            </button>
            <button 
              className="btn-save-appointment" 
              onClick={() => {
                if (attendedPatient?.patient_id) {
                  navigate(`/patients/${attendedPatient.patient_id}`);
                } else {
                  setAttendedPatient(null);
                }
              }}
              style={{ padding: '0 1.5rem' }}
            >
              IR A EVOLUCIONES
            </button>
          </>
        }
      >
        <div style={{ textAlign: 'center', padding: '1rem' }}>
          <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '1.25rem', fontWeight: '800', marginBottom: '1.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {attendedPatient?.patient_name}
          </div>
          <p style={{ color: 'white', fontSize: '1.15rem', fontWeight: '600', marginBottom: '0.75rem', lineHeight: '1.4' }}>
            Este Paciente ya ha sido marcado como "Consulta Finalizada"
          </p>
          <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.9rem' }}>
            Puede seguir ingresando a sus Evoluciones.
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default WaitingRoom;
